import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const AddTechnicianConsumableModal = ({ isOpen, onClose, onAdd, technicianId, technicianName }) => {
  const [formData, setFormData] = useState({
    consumable_id: '',
    quantity: 1
  });

  const [consumables, setConsumables] = useState([]);
  const [assignedConsumables, setAssignedConsumables] = useState([]);
  const [selectedConsumableStock, setSelectedConsumableStock] = useState(null);
  const [stockError, setStockError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && technicianId) {
      loadData();
    } else {
      // Reset form when modal closes
      setFormData({ consumable_id: '', quantity: 1 });
      setSelectedConsumableStock(null);
      setStockError('');
    }
  }, [isOpen, technicianId]);

  useEffect(() => {
    // Mettre à jour le stock disponible quand le consommable sélectionné change
    if (formData.consumable_id) {
      const selected = consumables.find(c => c.id === formData.consumable_id);
      setSelectedConsumableStock(selected?.current_stock || 0);
      
      // Vérifier si la quantité actuelle dépasse le stock
      if (parseInt(formData.quantity) > (selected?.current_stock || 0)) {
        setStockError(`Stock insuffisant. Disponible: ${selected?.current_stock || 0}`);
      } else {
        setStockError('');
      }
    } else {
      setSelectedConsumableStock(null);
      setStockError('');
    }
  }, [formData.consumable_id, consumables]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger tous les consommables avec leur stock actuel
      const { data: allConsumables, error: consumablesError } = await supabase
        ?.from('consumables')
        ?.select('*')
        ?.order('name');
      
      if (consumablesError) throw consumablesError;

      // Charger les consommables déjà assignés à ce technicien
      const { data: assigned, error: assignedError } = await supabase
        ?.from('consumables_techniciens')
        ?.select('consumable')
        ?.eq('technicien', technicianId);

      if (assignedError) throw assignedError;

      const assignedIds = assigned?.map(item => item.consumable) || [];
      
      setConsumables(allConsumables || []);
      setAssignedConsumables(assignedIds);
      
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (value) => {
    const quantity = parseInt(value) || 1;
    setFormData({ ...formData, quantity });
    
    // Vérifier le stock en temps réel
    if (selectedConsumableStock !== null && quantity > selectedConsumableStock) {
      setStockError(`Stock insuffisant. Disponible: ${selectedConsumableStock}`);
    } else {
      setStockError('');
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    // Vérification finale du stock
    if (selectedConsumableStock !== null && 
        (parseInt(formData.quantity) > selectedConsumableStock)) {
      setStockError(`Stock insuffisant. Disponible: ${selectedConsumableStock}`);
      return;
    }
    
    onAdd({
      technicien: technicianId,
      consumable: formData.consumable_id,
      quantity: parseInt(formData.quantity) || 1
    });
  };

  // Vérifier si un consommable est déjà assigné
  const isConsumableAssigned = (consumableId) => {
    return assignedConsumables.includes(consumableId);
  };

  // Filtrer pour n'avoir que les consommables non assignés dans les options disponibles
  const availableConsumables = consumables.filter(c => !isConsumableAssigned(c.id));

  const consumableOptions = consumables.map(c => ({
    value: c.id,
    label: `${c.name} (${c.unit})${c.category ? ` - ${c.category}` : ''} - Stock: ${c.current_stock || 0}`,
    disabled: isConsumableAssigned(c.id)
  }));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1020] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-card rounded-lg shadow-lg max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-in">
        <div className="p-6 border-b border-border sticky top-0 bg-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Icon name="PackagePlus" size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Ajouter un consommable</h3>
                <p className="text-sm text-muted-foreground">{technicianName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-muted rounded-lg transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <Select
                label="Consommable"
                options={consumableOptions}
                value={formData.consumable_id}
                onChange={(value) => setFormData({ ...formData, consumable_id: value })}
                placeholder="Sélectionner un consommable"
                required
              />

              {formData.consumable_id && selectedConsumableStock !== null && (
                <div className={`text-sm p-3 rounded-lg ${
                  selectedConsumableStock > 0 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <Icon name={selectedConsumableStock > 0 ? "CheckCircle" : "AlertCircle"} size={16} />
                    <span>
                      Stock disponible: <strong>{selectedConsumableStock}</strong>
                    </span>
                  </div>
                </div>
              )}

              {availableConsumables.length === 0 && (
                <div className="bg-muted/50 rounded-lg p-4 text-center">
                  <Icon name="Package" size={24} className="mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Tous les consommables ont déjà été assignés à ce technicien
                  </p>
                </div>
              )}

              <Input
                label="Quantité"
                type="number"
                min="1"
                max={selectedConsumableStock || 1}
                placeholder="1"
                value={formData.quantity}
                onChange={(e) => handleQuantityChange(e?.target?.value)}
                required
                error={stockError}
              />

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Icon name="Info" size={16} className="text-primary mt-0.5" />
                  <div className="text-xs text-primary">
                    <p className="font-medium mb-1">Information</p>
                    <p>Le consommable sera ajouté au stock du technicien avec la quantité spécifiée.</p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Plus"
              className="flex-1"
              disabled={
                !formData.consumable_id || 
                formData.quantity < 1 || 
                availableConsumables.length === 0 ||
                !!stockError ||
                (selectedConsumableStock !== null && parseInt(formData.quantity) > selectedConsumableStock)
              }
            >
              Ajouter
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddTechnicianConsumableModal;